
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter, List, Calendar, ChevronLeft, ChevronRight, Clock, MapPin, MessageSquare, AlertCircle } from 'lucide-react';
import { useAppContext } from '../../App.jsx';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { CHATS_COLLECTION } from '../../config/constants';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import AppointmentCard from '../../components/servi/AppointmentCard.jsx';
import FinalizeServiceModal from '../../components/chat/FinalizeServiceModal.jsx';

const FILTER_OPTIONS = [
    { label: 'Todos', value: 'Todos' },
    { label: 'Agendados', value: 'Agendado' },
    { label: 'Em Andamento', value: 'Em Andamento' },
    { label: 'Aguardando Aprovação', value: 'pending' },
    { label: 'Aceitos', value: 'accepted' },
    { label: 'Recusados', value: 'rejected' },
];

const FluxoListScreen = ({ isHistory }) => {
    const { db, user, sendInvoiceMessage } = useAppContext();
    const navigate = useNavigate();

    const [allAppointments, setAllAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const [serviceToFinalize, setServiceToFinalize] = useState(null);

    // Novas estados para a visão de Agenda
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewDate, setViewDate] = useState(new Date());

    const screenTitle = isHistory ? 'Histórico de Serviços' : 'Serviços Ativos e Próximos';

    useEffect(() => {
        if (!db || !user?.uid) {
            setIsLoading(false);
            return;
        }

        const q = query(collection(db, CHATS_COLLECTION), where("participants", "array-contains", user.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatsData = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    scheduledAt: doc.data().scheduledAt?.toDate() || null
                }))
                .filter(chat => chat.serviId === user.uid);

            setAllAppointments(chatsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Firebase query error:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, user?.uid]);

    // Lógica do Calendário
    const calendarDays = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
        return days;
    }, [viewDate]);

    const isSameDay = (d1, d2) =>
        d1 && d2 && d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();

    const filteredAppointments = useMemo(() => {
        const historyStatusList = ['Pago', 'Finalizado', 'Cancelado', 'Orçamento Recusado'];

        if (isHistory) {
            return allAppointments.filter(appt => historyStatusList.includes(appt.status));
        } else {
            let activeAppointments = allAppointments.filter(appt => !historyStatusList.includes(appt.status));

            if (activeFilter !== 'Todos') {
                 activeAppointments = activeAppointments.filter(appt => appt.status === activeFilter);
            }

            activeAppointments.sort((a, b) => {
                const dateA = a.lastMessageTimestamp?.seconds || 0;
                const dateB = b.lastMessageTimestamp?.seconds || 0;
                return dateB - dateA;
            });
            return activeAppointments;
        }
    }, [allAppointments, activeFilter, isHistory]);

    // Filtragem específica para o dia selecionado no calendário
    const appointmentsForSelectedDay = useMemo(() =>
        filteredAppointments.filter(a => isSameDay(a.scheduledAt, selectedDate))
    , [filteredAppointments, selectedDate]);

    const hasAppointmentOnDay = (date) =>
        filteredAppointments.some(a => isSameDay(a.scheduledAt, date));

    const handleFilterChange = (status) => { setActiveFilter(status); setIsFilterMenuOpen(false); };
    const handleOpenFinalizeModal = (appointment) => { setServiceToFinalize(appointment); };

    const handleFinalizeSubmit = (invoiceData) => {
        if (serviceToFinalize && invoiceData) {
            sendInvoiceMessage(serviceToFinalize.id, invoiceData);
        }
        setServiceToFinalize(null);
    };

    if (isLoading) return <AuthLoadingScreen message={`Carregando ${screenTitle.toLowerCase()}...`} />;

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50 text-left">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center min-w-0">
                    <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-1 shrink-0">
                        <ArrowLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <h2 className="text-xl font-bold text-gray-800 truncate">{screenTitle}</h2>
                </div>

                <div className="flex items-center space-x-1 shrink-0">
                    {!isHistory && (
                        <button
                            onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
                            className={`p-2.5 rounded-xl transition-all shadow-sm border ${viewMode === 'calendar' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-400 border-gray-100'}`}
                        >
                            {viewMode === 'list' ? <Calendar size={20} /> : <List size={20} />}
                        </button>
                    )}

                    {!isHistory && viewMode === 'list' && (
                        <div className="relative">
                            <button onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)} className="p-2.5 bg-white text-gray-400 rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-all">
                                <Filter size={20} />
                            </button>
                            {isFilterMenuOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl z-50 border border-gray-100 p-2 animate-slide-up">
                                    <p className='px-4 py-2 text-[10px] text-gray-400 font-black uppercase tracking-widest border-b border-gray-50 mb-1'>Filtrar Status</p>
                                    {FILTER_OPTIONS.map(option => (
                                        <button key={option.value} onClick={() => handleFilterChange(option.value)} className={`w-full text-left px-4 py-3 text-xs font-bold rounded-xl transition-all ${activeFilter === option.value ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-gray-600 hover:bg-gray-50'}`}>
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* CONTEÚDO DINÂMICO: LISTA OU CALENDÁRIO */}
            {viewMode === 'calendar' && !isHistory ? (
                <div className="flex flex-col flex-grow overflow-hidden">
                    {/* CALENDÁRIO COMPACTO INTEGRADO */}
                    <div className="bg-white rounded-[2rem] shadow-lg border border-gray-100 p-5 mb-6 shrink-0">
                        <div className="flex justify-between items-center mb-6 px-1">
                            <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-blue-600">
                                {viewDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
                            </h3>
                            <div className="flex space-x-1">
                                <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronLeft size={18}/></button>
                                <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronRight size={18}/></button>
                            </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center mb-2">
                            {['D','S','T','Q','Q','S','S'].map((d, i) => (
                                <span key={i} className="text-[9px] font-black text-gray-300 uppercase">{d}</span>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                            {calendarDays.map((date, i) => {
                                if (!date) return <div key={`empty-${i}`} className="aspect-square"></div>;
                                const active = isSameDay(date, selectedDate);
                                const hasJob = hasAppointmentOnDay(date);
                                const isToday = isSameDay(date, new Date());
                                return (
                                    <button key={i} onClick={() => setSelectedDate(date)} className={`relative aspect-square flex flex-col items-center justify-center rounded-xl text-xs font-bold transition-all ${active ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-gray-50 text-gray-700'} ${isToday && !active ? 'border-2 border-blue-100' : ''}`}>
                                        {date.getDate()}
                                        {hasJob && <span className={`absolute bottom-1.5 w-1 h-1 rounded-full ${active ? 'bg-white' : 'bg-blue-500 animate-pulse'}`}></span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* LISTA DO DIA SELECIONADO NA AGENDA */}
                    <div className="flex-grow overflow-y-auto space-y-3 pb-24 px-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                            {selectedDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' })}
                        </p>
                        {appointmentsForSelectedDay.length > 0 ? (
                            appointmentsForSelectedDay.map(appt => (
                                <div key={appt.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between group active:scale-[0.98] transition-all" onClick={() => navigate(`/servi/chat/${appt.id}`)}>
                                    <div className="flex items-center min-w-0">
                                        <div className="bg-blue-50 p-2.5 rounded-xl mr-3 shrink-0"><Clock className="text-blue-600" size={18} /></div>
                                        <div className="truncate">
                                            <p className="font-bold text-gray-800 text-sm">{appt.scheduledAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                                            <p className="text-[10px] text-gray-500 truncate font-medium">{appt.serviceTitle}</p>
                                        </div>
                                    </div>
                                    <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10 bg-white rounded-3xl border-2 border-dashed border-gray-100">
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Sem compromissos nesta data</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* VISÃO DE LISTA TRADICIONAL */
                <div className="space-y-4 flex-grow overflow-y-auto pb-24 pr-1">
                    {filteredAppointments.length > 0 ? (
                        filteredAppointments.map(appt => (
                            <AppointmentCard
                                key={appt.id}
                                appointment={appt}
                                onFinalize={handleOpenFinalizeModal}
                            />
                        ))
                    ) : (
                        <div className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-gray-100 px-8">
                            <List size={40} className="text-gray-200 mx-auto mb-3" />
                            <p className="font-black text-gray-400 uppercase tracking-widest text-xs">Nenhum serviço {isHistory ? 'no histórico' : 'ativo'}</p>
                        </div>
                    )}
                </div>
            )}

            {serviceToFinalize && (
                <FinalizeServiceModal
                    chat={serviceToFinalize}
                    onClose={() => setServiceToFinalize(null)}
                    onSubmit={handleFinalizeSubmit}
                />
            )}
        </div>
    );
};

export default FluxoListScreen;
